// Section 22 right panel warning list: last report's `warning` entries (narrated) plus live
// checks against the current draft and projection: Winter fuel short by N, no toolmaker for N
// seasons, seed not reserved, posts left vacant, shortages projected in the next four seasons,
// idle adults and stocks over capacity. The live checks read the game and the draft, so they
// show on the first screen, before any report.
import {
  catalog,
  narrateAll,
  type Decision,
  type GameState,
  type JobId,
  type NarrationTemplateId,
  type Projection,
  type Resource,
} from '../sim/api';
import { memo } from './memo';

/** Live checks with no report counterpart; their text lives in ui/text/en.ts `liveWarning`. */
export type LiveWarningId =
  | 'live.projected-short'
  | 'live.projected-short-later'
  | 'live.fuel-short'
  | 'live.unanswered'
  | 'live.tools-worn'
  | 'live.idle-adults'
  | 'live.over-capacity';

/**
 * M7: warn when the tools' mean durability is at or below this. The stock is fungible and keeps
 * its count until the mean reaches 0, then every tool breaks at once (ADR-0009), so the
 * `tools-short` report warning came a season too late to act on.
 */
const WORN_TOOLS = 0.3;

export interface WarningLine {
  /** 'report': last season's facts. 'live': this season's draft and projection. */
  source: 'report' | 'live';
  template: NarrationTemplateId | LiveWarningId;
  params: Record<string, string | number>;
}

const round1 = (x: number): number => Math.round(x * 10) / 10;

function effectiveJobs(game: GameState, draft: Decision | null): Map<JobId, number> {
  const counts = new Map<JobId, number>();
  for (const p of Object.values(game.people)) {
    const job = draft?.assignments[p.id] ?? p.job;
    counts.set(job, (counts.get(job) ?? 0) + 1);
  }
  return counts;
}

function fuelWarning(game: GameState, projection: Projection | null): WarningLine | null {
  if (projection === null || projection.baseTurn !== game.turn) return null;
  const winter = projection.seasons.find(
    (s) => (s.season === 'winter' || s.season === 'autumn') && s.shortages.includes('warmth'),
  );
  if (winter === undefined) return null;
  let short = 0;
  for (const need of Object.values(winter.needs)) short += Math.max(0, 1 - need.warmth);
  return {
    source: 'live',
    template: 'live.fuel-short',
    params: { season: winter.season, amount: round1(short) },
  };
}

function seedWarning(game: GameState): WarningLine | null {
  if (game.season !== 'spring' && game.season !== 'winter') return null;
  // Seed per plot and plots planted come from the last planting (data, not config).
  for (const report of [...game.history].reverse()) {
    const planted = report.entries.find((e) => e.kind === 'planted');
    if (planted?.kind !== 'planted' || planted.plots === 0) continue;
    const needed = planted.seedUsed;
    const missing = needed - game.seedReserved;
    return missing > 0.05
      ? {
          source: 'live',
          template: 'warning.seed-not-reserved',
          params: { amount: round1(missing) },
        }
      : null;
  }
  return null;
}

/**
 * Section 8 rest row: adults below the retirement age resting under the draft while able to work
 * (idle adults lose happiness and trust after four seasons; retirees are not idle, D-43; the
 * sick, injured and held rest because they must).
 */
function idleWarning(game: GameState, draft: Decision | null): WarningLine | null {
  const { adultAtSeasons, seasonsPerYear } = catalog();
  const age = game.policies.retirementAge;
  const retiredAt = age === 'never' ? Number.POSITIVE_INFINITY : age * seasonsPerYear;
  let count = 0;
  for (const p of Object.values(game.people)) {
    if ((draft?.assignments[p.id] ?? p.job) !== 'rest') continue;
    if (p.ageSeasons < adultAtSeasons || p.ageSeasons >= retiredAt) continue;
    if (p.status.sick !== null || p.status.injured !== null || p.status.inCustody !== null)
      continue;
    count++;
  }
  return count > 0 ? { source: 'live', template: 'live.idle-adults', params: { count } } : null;
}

/**
 * Section 7: stocks over capacity spoil at 3x. The seed shares food's store (ADR-0010 item 10)
 * except in Spring, when it is sown before the season's spoilage.
 */
function overCapacity(game: GameState): WarningLine[] {
  const out: WarningLine[] = [];
  const { spoilage, overCapacitySpoilageMul: mul } = catalog();
  const seed = game.season === 'spring' ? 0 : game.seedReserved;
  for (const r of Object.keys(spoilage) as Resource[]) {
    const stock = game.stocks[r];
    const held = stock.amount + (r === 'food' ? seed : 0);
    const excess = held - stock.capacity;
    if (spoilage[r] > 0 && excess >= 0.05)
      out.push({
        source: 'live',
        template: 'live.over-capacity',
        params: { resource: r, amount: round1(excess), mul },
      });
  }
  return out;
}

export const warningsVM = memo(
  (game: GameState, draft: Decision | null, projection: Projection | null): WarningLine[] => {
    const out: WarningLine[] = [];
    const last = game.history[game.history.length - 1];
    if (last !== undefined) {
      const warnings = last.entries.filter((e) => e.kind === 'warning');
      for (const n of narrateAll(warnings, game))
        out.push({ source: 'report', template: n.template, params: n.params });
    }
    const unanswered = game.openPrompts.filter((p) => !(p.id in (draft?.eventChoices ?? {})));
    if (unanswered.length > 0)
      out.push({
        source: 'live',
        template: 'live.unanswered',
        params: { count: unanswered.length },
      });
    const seasons = projection?.baseTurn === game.turn ? projection.seasons : [];
    const next = seasons[0];
    for (const resource of next?.shortages ?? [])
      if (resource !== 'warmth')
        out.push({ source: 'live', template: 'live.projected-short', params: { resource } });
    // M7: a food or water shortage later in the projection is flagged now, with its season
    // (a Lean start runs out in its first Summer; the warning came only in Summer).
    for (const resource of ['food', 'water'] as const) {
      if (next?.shortages.includes(resource) === true) continue;
      const later = seasons.slice(1).find((s) => s.shortages.includes(resource));
      if (later !== undefined)
        out.push({
          source: 'live',
          template: 'live.projected-short-later',
          params: { resource, season: later.season },
        });
    }
    const fuel = fuelWarning(game, projection);
    if (fuel !== null) out.push(fuel);
    const jobs = effectiveJobs(game, draft);
    const toolmakerLast = game.lastWorkedTurn.toolmaker;
    const idleSeasons = game.turn - (toolmakerLast ?? 0);
    if ((jobs.get('toolmaker') ?? 0) === 0 && idleSeasons >= 2)
      out.push({
        source: 'live',
        template: 'warning.no-toolmaker',
        params: { seasons: idleSeasons },
      });
    if (game.stocks.tools.amount >= 1 && game.toolDurability <= WORN_TOOLS)
      out.push({
        source: 'live',
        template: 'live.tools-worn',
        params: { percent: Math.round(game.toolDurability * 100) },
      });
    const seed = seedWarning(game);
    if (seed !== null) out.push(seed);
    const idle = idleWarning(game, draft);
    if (idle !== null) out.push(idle);
    out.push(...overCapacity(game));
    const assignedNow = new Set(Object.values(draft?.assignments ?? {}));
    for (const e of last?.entries ?? []) {
      if (e.kind !== 'job-vacant' || assignedNow.has(e.jobId)) continue;
      out.push({ source: 'live', template: 'warning.job-vacant', params: { job: e.jobId } });
    }
    // A live check that repeats last season's warning adds nothing; keep the report's line.
    const reported = new Set(out.filter((w) => w.source === 'report').map((w) => w.template));
    return out.filter((w) => w.source === 'report' || !reported.has(w.template));
  },
);
